/* eslint-disable react-hooks/exhaustive-deps */
import { Autocomplete, Box, Button, Checkbox, Divider, Drawer, FormControl, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, InputLabel, OutlinedInput, styled, Switch, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { LoadingPage } from "../../../../common/LoadingPage";
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import { apiRequest } from "../../../../common/common";
import { ClearIcon } from "@mui/x-date-pickers";
import { Addition, CurrencyJSON, Deductions, TaxableOption } from "../../../../common/const";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AdditionDeduction from "./AdditionDeduction";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const DemoBox1 = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 5px 5px 15px'
}));

const EditTotalBudget = ({
    projectId,
    totalBudgetOpen,
    setTotalBudgetOpen,
    projectData,
    getData,
    budget,
}) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [approvedBudget, setApprovedBudget] = useState(null);
    const [taxableOption, setTaxableOption] = useState(null);
    const [equipmentCost, setEquipmentCost] = useState(0);
    const [isTax, setIsTax] = useState(false);
    const [budgetDescription, setBudgetDescription] = useState(null);
    const [additions, setAdditions] = useState(Addition);
    const [deductions, setDeductions] = useState(Deductions);

    function updateItemValues(additions, budget) {
        const updated = { ...additions };

        const costToAdditionMap = {
            total_installation: 'installation',
            total_freight: 'freight',
            total_storage: 'storage',
            total_fees: 'fees',
            total_discounts: 'discount',
            total_tradein: 'reuse'
        };

        for (const [costKey, additionKey] of Object.entries(costToAdditionMap)) {
            const totalValue = Number(budget[costKey]);
            const totalCost = budget.total_cost || 1; // avoid division by 0

            const type = additions[additionKey]?.actual?.type;

            if (updated[additionKey]?.actual) {
                updated[additionKey].actual.itemValue =
                    type === 'rate'
                        ? parseFloat(((totalValue * 100) / totalCost).toFixed(2))
                        : totalValue;
            }
        }

        return updated;
    }

    const getInitialProjectData = async () => {
        try {
            setDataLoading(true);
            const response = await apiRequest('get', `/project/${projectId}`);
            const { data } = response.data;
            setApprovedBudget(data?.total_approved_budget);
            setTaxableOption(JSON.parse(data?.taxable_option));
            setEquipmentCost(data?.approved_equipment_cost || 0);
            setIsTax(data?.is_tax_exempt);
            setBudgetDescription(data?.budget_description);
            if (data?.additions !== null) {
                const updatedAdditions = updateItemValues(data?.additions, budget);
                setAdditions(updatedAdditions);
            } else {
                setAdditions((prevData) => ({
                    ...prevData,
                    tax: {
                        ...prevData.tax,
                        approved: {
                            ...prevData.tax.approved,
                            value: data?.tax_rate
                        },
                        actual: {
                            ...prevData.tax.actual,
                            value: data?.tax_rate
                        }
                    }
                }))
            }
            if (data?.deductions !== null) {
                const updatedDeductions = updateItemValues(data?.deductions, budget);
                setDeductions(updatedDeductions);
            }
            setDataLoading(false);
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }
    useEffect(() => {
        if (projectId) {
            getInitialProjectData();
        }
    }, [projectId]);

    const handleClose = () => {
        setTotalBudgetOpen(false);
        setBudgetDescription(null);
        setTaxableOption(null);
        setApprovedBudget(null);
        setIsTax(false);
        setEquipmentCost(null);
        setAdditions(Addition);
        setDeductions(Deductions);
    }

    const handleSubmit = async () => {
        try {
            setDataLoading(true);
            const data = {
                additions: additions,
                deductions: deductions,
                budget_description: budgetDescription,
                taxable_option: JSON.stringify(taxableOption),
                is_tax_exempt: isTax,
                total_approved_budget: Number(approvedBudget),
                approved_equipment_cost: equipmentCost,
                tax_rate: additions?.tax?.actual?.value,
                name: projectData?.name,
                organization_id: projectData?.organization_id,
                owner_id: projectData?.owner_id,
                country: projectData?.country,
            };
            const response = await apiRequest('put', `/project/${projectId}`, data);
            if (response?.data) {
                showNotification(response?.data.message, 'success');
                setDataLoading(false);
                getData();
                handleClose();
            }
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    function getAdjustmentValue(entry, base) {
        if (entry.approved.type === 'value') {
            return Number(entry.approved.value);
        } else {
            return (Number(base) * Number(entry.approved.value)) / 100;
        }
    }

    useEffect(() => {
        let cost = Number(approvedBudget);

        const additionKeys = [
            'tax', 'installation', 'freight', 'fees', 'storage',
            'contingency', 'inflation'
        ];

        for (const key of additionKeys) {
            if (additions[key]?.approved) {
                cost -= getAdjustmentValue(additions[key], approvedBudget);
            }
        }

        const deductionKeys = ['discount', 'reuse'];

        for (const key of deductionKeys) {
            if (deductions[key]?.approved) {
                cost += getAdjustmentValue(deductions[key], approvedBudget);
            }
        }

        setEquipmentCost(cost);
    }, [additions, deductions]);

    return (
        <Drawer
            anchor="bottom"
            open={totalBudgetOpen}
            sx={{
                '& .MuiDrawer-paper': {
                    height: '89%',
                }
            }}
        >
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
            <DemoBox1>
                <Typography variant='body1' style={{ fontWeight: 'bold' }}>Edit Total Project Budget</Typography>
                <Box>
                    <Button variant='contained' size="small" onClick={() => handleSubmit()}>
                        Save
                    </Button>
                    <IconButton onClick={() => handleClose()}>
                        <ClearIcon />
                    </IconButton>
                </Box>
            </DemoBox1>
            <Divider />
            <Grid container spacing={1} pt={2} pl={1} pr={1}>
                <Grid item xs={12} md={3}>
                    <FormControl size="small" fullWidth variant="outlined">
                        <InputLabel htmlFor="standard-adornment-password">Total Approved Project Budget</InputLabel>
                        <OutlinedInput
                            label="Total Approved Project Budget"
                            id="standard-adornment-password"
                            type='number'
                            startAdornment={<InputAdornment position="start">{CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol}</InputAdornment>}
                            value={approvedBudget || ''}
                            onChange={(e) => {
                                setApprovedBudget(e.target.value)
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormControl size="small" fullWidth variant="outlined">
                        <InputLabel htmlFor="standard-adornment-password">Equipment</InputLabel>
                        <OutlinedInput
                            label="Equipment"
                            id="standard-adornment-password"
                            type='number'
                            startAdornment={<InputAdornment position="start">{CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol}</InputAdornment>}
                            value={equipmentCost || ''}
                            disabled
                            onChange={(e) => {
                                setEquipmentCost(e.target.value)
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        value={TaxableOption.filter((c) => taxableOption?.includes(c.value)) || []}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={TaxableOption}
                        multiple
                        clearIcon={null}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setTaxableOption(newValue?.map((a) => a.value));
                        }}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps } = props;
                            return (
                                <li key={key} {...optionProps}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {option?.name}
                                </li>
                            );
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Taxable Option" name="Taxable Option" variant="outlined" />}
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormGroup>
                        <FormControlLabel
                            control={<Switch checked={isTax} onClick={(e) => {
                                setIsTax(e.target.checked)
                            }} />}
                            label="Project Tax Exempt Status"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px' } }}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <AdditionDeduction
                        additions={additions}
                        setAdditions={setAdditions}
                        deductions={deductions}
                        setDeductions={setDeductions}
                        projectData={projectData}
                        budget={budget}
                    />
                </Grid>
                <Grid item xs={12} md={12} mb={1}>
                    <FormControl size="small" fullWidth variant="outlined">
                        <InputLabel htmlFor="standard-adornment-password">Budget Description</InputLabel>
                        <OutlinedInput
                            label="Budget Description"
                            id="standard-adornment-password"
                            type='text'
                            multiline
                            minRows={3}
                            maxRows={3}
                            value={budgetDescription || ''}
                            onChange={(e) => {
                                setBudgetDescription(e.target.value);
                            }}
                        />
                    </FormControl>
                </Grid>

            </Grid>
        </Drawer>
    )
}

export default EditTotalBudget;